import React from 'react';
import {
  FormControl,
  FormLabel,
  Select,
  Option,
  IconButton,
  Tooltip
} from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import { formatTitle } from '../../helpers/policyUtils';
import { getProviderKind } from '../../helpers/modelProviders';
import RenderField from './RenderField';

/**
 * Settings step content with functions and user assignment
 */
const SettingsStep = ({
  currentPolicy,
  setCurrentPolicy,
  currentStructure,
  // Functions
  functions,
  isLoadingFunctions,
  // Users
  users,
  isLoadingUsers,
  currentUserId,
  models = [],
  // Evaluation-specific
  compliancePolicies = [],
  isEvaluationType = false
}) => {
  const getSettingOptions = (setting) => {
    if (setting?.optionsFromApi === 'models') {
      return models
        .filter((model) => {
          const modelKind = model?.kind || getProviderKind(model?.provider);
          return model?.is_active === true && modelKind === 'vision';
        })
        .map((model) => ({
          value: model.id,
          label: model.display_name || model.name
        }));
    }

    return setting?.options || [];
  };

  const handleSettingChange = (key, newValue) => {
    const updatedPolicy = {
      ...currentPolicy,
      settings: {
        ...currentPolicy.settings,
        [key]: newValue
      }
    };
    setCurrentPolicy(updatedPolicy);
  };

  const handleLinkedPolicyChange = (policyId) => {
    const updatedPolicy = {
      ...currentPolicy,
      settings: {
        ...currentPolicy.settings,
        linked_policy_id: policyId
      }
    };
    setCurrentPolicy(updatedPolicy);
  };

  const handleFunctionsChange = (newValue) => {
    const updatedPolicy = {
      ...currentPolicy,
      post_complete_functions: newValue
    };
    setCurrentPolicy(updatedPolicy);
  };

  const handleUsersChange = (newValue) => {
    const updatedPolicy = {
      ...currentPolicy,
      users: newValue || currentPolicy.users
    };
    setCurrentPolicy(updatedPolicy);
  };

  return (
    <>
      {/* Linked Compliance Policy (for Evaluation type) */}
      {isEvaluationType && (
        <FormControl sx={{ alignSelf: 'flex-start', mb: 2 }}>
          <FormLabel sx={{ textTransform: 'capitalize' }}>
            Linked Compliance Policy:
            <Tooltip title="Select the compliance policy to use for field definitions" arrow>
              <IconButton sx={{ ml: 1 }} size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </FormLabel>
          <Select
            value={currentPolicy.settings?.linked_policy_id || ''}
            onChange={(_, newValue) => handleLinkedPolicyChange(newValue)}
            placeholder="Select a compliance policy"
          >
            {compliancePolicies.map(policy => (
              <Option key={policy.id} value={policy.id}>
                {policy.name}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Dynamic settings from structure */}
      {currentStructure.settings.map(setting => {
        const options = getSettingOptions(setting);

        return (
          <FormControl key={setting.key} sx={{ alignSelf: 'flex-start' }}>
            <FormLabel sx={{ textTransform: 'capitalize' }}>
              {formatTitle(setting.key)}:
              <Tooltip title={setting.description} arrow>
                <IconButton sx={{ ml: 1 }} size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </FormLabel>
            <RenderField
              type={setting.type}
              value={currentPolicy.settings[setting.key]}
              options={options}
              onChange={newValue => handleSettingChange(setting.key, newValue)}
            />
          </FormControl>
        );
      })}

      {/* Post-Complete Functions */}
      <FormControl sx={{ alignSelf: 'flex-start', mt: 2 }}>
        <FormLabel sx={{ textTransform: 'capitalize' }}>
          Post-Completed Functions:
          <Tooltip title="Select functions to run after policy execution" arrow>
            <IconButton sx={{ ml: 1 }} size="small">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </FormLabel>
        <Select
          multiple
          value={currentPolicy.post_complete_functions || []}
          onChange={(_, newValue) => handleFunctionsChange(newValue)}
          disabled={isLoadingFunctions}
          placeholder={isLoadingFunctions ? "Loading functions..." : "Select functions"}
        >
          {functions
            .filter(func => func.function_type === "post-complete")
            .map(func => (
              <Option key={func.id} value={func.id}>
                <span>{func.name}</span>
                <Tooltip title={func.description || 'No description available'} arrow placement="right">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Option>
            ))}
        </Select>
      </FormControl>

      {/* Assigned Users */}
      <FormControl sx={{ alignSelf: 'flex-start', mt: 2 }}>
        <FormLabel sx={{ textTransform: 'capitalize' }}>
          Assigned Users:
          <Tooltip title="Select users to assign to this policy" arrow>
            <IconButton sx={{ ml: 1 }} size="small">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </FormLabel>
        <Select
          multiple
          value={currentPolicy.users || []}
          onChange={(_, newValue) => handleUsersChange(newValue)}
          disabled={isLoadingUsers}
          placeholder={isLoadingUsers ? "Loading users..." : "Select users"}
        >
          {users.map(user => (
            <Option key={user.id} value={user.id}>
              {user.username} {user.id === parseInt(currentUserId) ? "(you)" : ""}
            </Option>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export default SettingsStep;
